import { formatDateTime, formatDateTimeNoTimeZoneForReport, formatDate, timeFromDate } from "@/src/date-helpers";
import {SaveStates} from "@/src/enums";
import { APIModel } from "../../api-model";
import { Assignment } from "../../assignment";
import { Donor } from "../../donor";
import { FinalDisposition } from "../../final-disposition";
import { MatchEffectiveValue } from "../../match-effective-value";
import { Organ } from "../../organ";
import { TransplantCenter } from "../../transplant-center";
import { CoordinatorNote } from "../../coordinator-note";
import { Status } from "../../status";
import {EP, APIRouteFull} from "@/src/endpoints";
import { AppliedTag } from "../../match/applied-tag";
import { ChecklistApplication } from "../../match/checklist/checklist-application";


type BoolYNU = {
  value: boolean;
  display: string;
}
/** 
 * TODO: Combine this with internal QA row somehow
 */
function formatNextStep<T>(fieldsToMap: Array<string>, from: any, to: T) {
  fieldsToMap.forEach((k) => {
    let display = ``;

    if(from.status) {
      if(from.status.next_step) display += `${from.status.next_step}`
 
      if(from.extra_next_step && from.extra_next_step.name) {
        if(from.status.next_step) display += ` + `;
        display += `${from.extra_next_step.name}`;
      } 
      if(from.extra_next_step) {
        if(from.extra_next_step.identifier == 'bypass' && from.bypassed_offer) {
          display += ` ${from.bypass_codes.includes(from.bypassed_offer.decline_code_1) ? `${from.bypassed_offer.decline_code_1},` : ''}
                            ${from.bypass_codes.includes(from.bypassed_offer.decline_code_2) ? from.bypassed_offer.decline_code_2 : ''}
                            at Seq # ${from.bypassed_offer.seq}`
        }
        else if(from.extra_next_step.identifier == 'under_evaluation') {
          display += `${from.under_evaluation_offer.seq}`;
        }
      }
    }
    to[k] = display || "N/A";
  });
}


export class SuperTeamDashboardRow extends APIModel {
  id: string;
  code: string;
  donor_id: string;
  organ_id: string;
  transplant_center_id: string;
  opo_id: string;
  offer_date: string
  decline_note: string;
  donor: Donor;
  transplantCenter: TransplantCenter;
  organ: Organ;
  coordinator_note: CoordinatorNote;
  auto_rule_out: boolean;
  case: boolean;
  physician: string;
  final_disposition: FinalDisposition;
  match_effective_value: MatchEffectiveValue;
  assignment: Assignment;
  elapsed_time: string;
  last_update: string;
  next_step: string;
  status: Status;
  tags!: AppliedTag[];
  checklists!: ChecklistApplication[];
  checklist!: { status: string; checklist_name: string; checklist_started_at: { display: string }; verified_at: { display: string }; verified_by: { full_name: string } };

  dirty: boolean;
  current_state: string;
  final_disposition_approved: boolean;
  service_level_approved: boolean;
  errors: any;

  static fieldsToMap = {
    direct: [
      'id',
      'code',
      'transplant_center_id',
      'donor_id',
      'organ_id',
      'status_id',
      'extra_next_step_id',
      'decline_note',
      'physician',
      'email_url',
      'offer_date',
      'provisional_yes',
      'under_evaluation',
      'refused',
      'expedited',
      'donoredit_page_visited',
      'acceptancelist_page_visited',
      'matchlist_page_visited',
      'dismissed',
      'created_at',
      'updated_at',
      'case',
      'acceptancelist_page_visited_at',
      'matchlist_page_visited_at',
      'donoredit_page_visited_at',
      'latest_offer_email_at',
      'dismissed_at',
      'made_case_at',
      'revoked',
      'auto_rule_out',
      'final_disposition_id',
      'final_disposition_notes',
      'first_prov_yes_seq_any_tc',
      'second_prov_yes_seq_any_tc',
      'service_level_override',
      'latest_primary_backup_email_at',
      'sequence_status_page_visited',
      'sequence_status_page_visited_at',
      'bypass',
      'outcome_id',
      'physician_consulted',
      'add_manually',
      'created_by',
      'updated_by',
      'first_transplant_details',
      'second_transplant_details',
      'bypassed_offer',
      'derive_combined_recipients_physician',
      'derive_combined_recipients_notes',
      'final_disposition_approved_at',
      'service_level_approval_at',
      'assignment'
    ],
    booleanYesNo: [
      'revoked',
      'expedited'
    ],
    directNA: [
    ],
    formatDateTime: [
      'offer_date'
    ],
    timeFromDate: [
      'elapsed_time'
    ],
    formatDate: [
      'last_update'
    ],
    formatNextStep: [
      'next_step'
    ],
    dates: [
    ],
    arrays: [
    ]
  }

  static buildFromAPI(record: any): SuperTeamDashboardRow {
    const row = new SuperTeamDashboardRow();
    record = record || {};

    this.mapFieldsDirectly<SuperTeamDashboardRow>(SuperTeamDashboardRow.fieldsToMap.direct, record, row);
    this.mapBooleanYesNo<SuperTeamDashboardRow>(SuperTeamDashboardRow.fieldsToMap.booleanYesNo, record, row);
    this.mapFieldsNA<SuperTeamDashboardRow>(SuperTeamDashboardRow.fieldsToMap.directNA, record, row);
    this.mapFormatDateTime<SuperTeamDashboardRow>(SuperTeamDashboardRow.fieldsToMap.formatDateTime, record, row);
    this.mapTimeFromDate<SuperTeamDashboardRow>(SuperTeamDashboardRow.fieldsToMap.timeFromDate, record.offer_date, row);
    this.mapLastUpdateDate<SuperTeamDashboardRow>(SuperTeamDashboardRow.fieldsToMap.formatDate, record.acceptancelist_page_visited_at, record.offer_date, row);
    formatNextStep<SuperTeamDashboardRow>(SuperTeamDashboardRow.fieldsToMap.formatNextStep, record, row);

    row.donor = Donor.buildFromAPI(record.donor);
    row.transplantCenter = TransplantCenter.buildFromAPI(record.transplant_center);
    row.organ = Organ.buildFromAPI(record.organ);
    row.coordinator_note = CoordinatorNote.buildFromAPI(record.coordinator_note);
    row.final_disposition = FinalDisposition.buildFromAPI(record.final_disposition);
    row.match_effective_value = MatchEffectiveValue.buildFromAPI(record.match_effective_value);
    row.assignment = Assignment.buildFromAPI(record.assignment);
    row.status = Status.buildFromAPI(record.status);
    row.dirty = false;
    row.final_disposition_approved = record.final_disposition_approved_at ? true : false;
    row.service_level_approved = record.service_level_approval_at ? true : false; 
    row.current_state = SaveStates.Blank;

    row.tags = [];
    if(record.applied_tags) {
      record.applied_tags.forEach((tag: any) => {
        row.tags.push(AppliedTag.buildFromAPI(tag));
      })
    }

    row.checklists = [];
    if(record.checklists) {
      record.checklists.forEach((checklist: any) => {
        row.checklists.push(ChecklistApplication.buildFromAPI(checklist));
      })
    }

    row.checklist = {
      status: row.checklists.map(c => c.status_display).join('\n ') || 'Not Started',
      checklist_name: row.checklists.map(c => c.checklist_name).join('\n '),
      checklist_started_at: { display: row.checklists.map(c => c.checklist_started_at?.display).join('\n ') },
      verified_at: { display: row.checklists.map(c => c.verified_at?.display).join('\n ') },
      verified_by: { full_name: row.checklists.map(c => c.verified_by?.full_name).join('\n ') }
    };

    return row;
  }

  getRowClass(data) {
    if(!data) return '';

    if(data.current_state === SaveStates.Errored) return 'row-failed';
    if(data.current_state === SaveStates.Processing) return 'row-saving';
    if(data.current_state === SaveStates.Success) return 'row-saved';
    if(data.current_state === SaveStates.Idle) return '';
    if(data.dirty) return 'row-edited';
  }

  onModelValueUpdate(data, index, col, val) {
    this.setNestedValue(data, col.field, val); 
    if (col.field === 'auto_rule_out') {
      this.setPhysician(data, val);
    }
    data.dirty = true;
    data.current_state = SaveStates.Blank;
  }

  setPhysician(data, aro){
    if (aro === true && !data.physician) {
      this.setNestedValue(data, 'physician', 'Auto rule out');
    } else if (aro === false && data.physician === 'Auto rule out') {
      this.setNestedValue(data, 'physician', null);
    }
  }

  getNestedValue(obj,path) {
    return path.split('.').reduce((acc,part) => acc?.[part], obj)
  }

  setNestedValue(obj, path, value) {
    const keys = path.split('.')
    const last = keys.pop()
    const target = keys.reduce((acc,part) => acc[part] ??= {}, obj)
    target[last] = value
  }

  get logisticsQuickQuoteURL() {
    return APIRouteFull(EP.logistics.quick_quote,{ match_id: this.id })
  }
}
